/* eslint-disable no-param-reassign */
import Vue from 'vue';
import { gsap } from 'gsap';
import imagesLoaded from 'imagesloaded';

Vue.component('home-hero-slider-component', {
    props: {
        slideAmount: {
            type: Number,
            default: 0,
        },

        sliderIntervalTime: {
            type: Number,
            default: 6,
        },
    },
    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            currentIndex: 0,
            maxIndex: 0,
            inTransition: false,
            imageHolderWidth: null,
            imageHolderHeight: null,
            stepWidth: null,
            sliderInterval: null,
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.maxIndex = this.slideAmount - 1;

        this.$el.querySelector('.home-hero-slider__slider-holder').classList.add('--visible');

        imagesLoaded(this.$el, () => {
            const imageHolder = this.$el.querySelector('.home-hero-slider__slider-image-holder');

            if (window.matchMedia('(min-width: 768px)').matches) {
                this.imageHolderWidth = imageHolder.getBoundingClientRect().width;
                this.imageHolderHeight = imageHolder.getBoundingClientRect().height;

                gsap.set(imageHolder, { clipPath: `circle(0px at ${this.imageHolderWidth}px ${this.imageHolderHeight}px)` });

                gsap.delayedCall(1, () => {
                    gsap.to(imageHolder, {
                        duration: 1.2,
                        clipPath: `circle(${this.imageHolderWidth}px at ${this.imageHolderWidth}px ${this.imageHolderHeight}px)`,
                        ease: 'power4.out',
                    });
                });
            }

            this.stepWidth = this.$el.querySelector('.home-hero-slider__controls-progress-bar-holder').getBoundingClientRect().width / (this.maxIndex + 1);

            gsap.set('.home-hero-slider__slider-image-holder', { autoAlpha: 1 });

            gsap.delayedCall(1, () => {
                gsap.to(imageHolder.querySelector('img'), { duration: 1.2, scale: 1, ease: 'power4.out' });
                gsap.to(this.$el.querySelector('.home-hero-slider__controls-holder'), {
                    duration: 1, autoAlpha: 1, ease: 'power4.out', delay: 0.5,
                });

                gsap.to(['.--visible .home-hero-slider__slider-title-holder',
                    '.--visible .home-hero-slider__slider-text-holder',
                    '.--visible .home-hero-slider__slider-cta-holder'], {
                    duration: 1, autoAlpha: 1, y: 0, stagger: 0.06, ease: 'power4.out',
                });
            });
        });

        this.initSliderInterval();

        EventBus.$on('window-resize', () => {
            this.resizeSlider();
        });
    },

    computed: {
        humanReadableSlideIndex() {
            if (this.currentIndex + 1 < 10) {
                return `0${this.currentIndex + 1}`;
            }

            return this.currentIndex + 1;
        },

        humanReadableMaxIndex() {
            if (this.maxIndex + 1 < 10) {
                return `0${this.maxIndex + 1}`;
            }

            return this.maxIndex + 1;
        },
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        initSliderInterval() {
            this.sliderInterval = setInterval(() => {
                this.handleNextSlide();
            }, (this.sliderIntervalTime * 1000));
        },

        destroySliderInterval() {
            clearInterval(this.sliderInterval);
            this.sliderInterval = null;
        },

        reInitSliderInterval() {
            clearInterval(this.sliderInterval);
            this.sliderInterval = null;

            this.initSliderInterval();
        },

        handlePreviousSlide() {
            if (this.inTransition) {
                return;
            }

            if (this.currentIndex === 0) {
                this.currentIndex = this.maxIndex;
                this.reInitSliderInterval();
                return;
            }

            this.reInitSliderInterval();
            this.currentIndex -= 1;
        },

        handleNextSlide() {
            if (this.inTransition) {
                return;
            }

            if (this.currentIndex === this.maxIndex) {
                this.reInitSliderInterval();
                this.currentIndex = 0;
                return;
            }

            this.reInitSliderInterval();
            this.currentIndex += 1;
        },

        beforeEnter(el) {
            this.inTransition = true;
            const imageHolder = el.querySelector('.home-hero-slider__slider-image-holder');

            imageHolder.style.zIndex = 1;

            if (window.matchMedia('(min-width: 768px)').matches) {
                gsap.set(imageHolder, { clipPath: 'circle(0px at 720px 720px)' });
            }

            gsap.set(imageHolder.querySelector('img'), { scale: 1.4 });

            gsap.set(el.querySelector('.home-hero-slider__slider-container'), { autoAlpha: 1 });
        },

        enter(el, done) {
            const imageHolder = el.querySelector('.home-hero-slider__slider-image-holder');

            if (window.matchMedia('(min-width: 768px)').matches) {
                gsap.to(imageHolder, {
                    duration: 1.4,
                    clipPath: `circle(${this.imageHolderWidth}px at ${this.imageHolderWidth}px ${this.imageHolderHeight}px)`,
                    ease: 'power4.out',
                });
            }

            gsap.to(imageHolder.querySelector('img'), {
                duration: 1.2, scale: 1, ease: 'power4.out', onComplete: done,
            });

            gsap.to([
                el.querySelector('.home-hero-slider__slider-title-holder'),
                el.querySelector('.home-hero-slider__slider-text-holder'),
                el.querySelector('.home-hero-slider__slider-cta-holder')], {
                duration: 0.7, autoAlpha: 1, stagger: 0.1, delay: 0.4, ease: 'power4.out',
            });

            gsap.to([
                el.querySelector('.home-hero-slider__slider-title-holder'),
                el.querySelector('.home-hero-slider__slider-text-holder'),
                el.querySelector('.home-hero-slider__slider-cta-holder')], {
                duration: 1.2, y: 0, stagger: 0.1, delay: 0.4, ease: 'power4.out',
            });
        },

        afterEnter(el) {
            el.classList.add('--visible');
            el.querySelector('.home-hero-slider__slider-image-holder').style.zIndex = '';
            this.inTransition = false;
        },

        // --------
        // LEAVING
        // --------

        leave(el, done) {
            gsap.to(el.querySelector('.home-hero-slider__slider-container'), {
                duration: 0.5,
                autoAlpha: 0,
                ease: 'power4.out',
            });

            gsap.to(el.querySelector('.home-hero-slider__slider-image-holder img'), {
                duration: 1.2,
                scale: 1,
                ease: 'power4.out',
                onComplete: done,
            });
        },

        afterLeave(el) {
            el.classList.remove('--visible');

            const imageHolder = el.querySelector('.home-hero-slider__slider-image-holder');

            if (window.matchMedia('(min-width: 768px)').matches) {
                gsap.set(imageHolder, { clipPath: `circle(0px at ${this.imageHolderWidth}px ${this.imageHolderHeight}px)` });
            }

            gsap.set(imageHolder.querySelector('img'), { scale: 1.4 });
            gsap.set([
                el.querySelector('.home-hero-slider__slider-title-holder'),
                el.querySelector('.home-hero-slider__slider-text-holder'),
                el.querySelector('.home-hero-slider__slider-cta-holder')], {
                autoAlpha: 0, y: -20,
            });
        },

        resizeSlider() {
            const imageHolder = this.$el.querySelector('.--visible .home-hero-slider__slider-image-holder');
            this.imageHolderWidth = imageHolder.getBoundingClientRect().width;
            this.imageHolderHeight = imageHolder.getBoundingClientRect().height;

            Vue.nextTick(() => {
                if (window.matchMedia('(min-width: 768px)').matches) {
                    gsap.set(this.$el.querySelector('.home-hero-slider__slider-holder.--visible .home-hero-slider__slider-image-holder'), {
                        clipPath: `circle(${this.imageHolderWidth}px at ${this.imageHolderWidth}px ${this.imageHolderHeight}px)`,
                    });
                } else {
                    gsap.set('.--visible .home-hero-slider__slider-image-holder', {
                        clipPath: '',
                    });
                }

                this.stepWidth = this.$el.querySelector('.home-hero-slider__controls-progress-bar-holder').getBoundingClientRect().width / (this.maxIndex + 1);
            });
        },
    },
    onDestroy() {
        clearInterval(this.sliderInterval);
        this.sliderInterval = null;
    },
});
